// The image-based lighting environment the R160 reflects.
//
// The shell is `R160_Stainless` at metallic 0.90 — physically chrome already. What it
// was missing is something to BE chrome about: a metal has no diffuse albedo, so under
// deck's `_lighting: 'pbr'` with punctual lights only it gets two specular highlights
// and is otherwise black. That is the near-black train, and no base colour or roughness
// value fixes it, because the term that carries a mirror finish (`getIBLContribution`)
// is compiled out entirely unless `_imageBasedLightingEnvironment` is supplied.
//
// glTF IBL wants three textures — a diffuse irradiance cube, a specular radiance cube,
// and the split-sum BRDF lookup table. Rather than ship photographic cubemaps (six
// files per cube, plus a LUT, in a repo whose only other binary assets are the train
// itself), all three are generated here: the environment is an analytic sky/ground
// gradient, which is exactly the studio setup a chrome product shot uses, and the LUT
// is the standard Karis integration, which has a closed form and is cheap at this size.
//
// ⚠️ The shader wraps EVERY one of these in `SRGBtoLINEAR` and `MANUAL_SRGB` is on
// (`@luma.gl/gltf/parsers/parse-pbr-material`). Canvas output is already sRGB so the
// cubes are natural, but the BRDF LUT holds two numbers that are NOT colours, and it
// has to be written sRGB-encoded so the shader's decode hands back what was integrated.
import type { PBREnvironment } from '@luma.gl/gltf'
import type { Device, Texture } from '@luma.gl/core'

type Rgb = readonly [number, number, number]

/**
 * Which component of the reflection vector points at the sky. deck's common space is
 * +Z up and the PBR module reflects in that space, so the gradient runs along z.
 * Named because it is the one thing here that is a guess about someone else's
 * convention: if the sky ever lands on the train's flank instead of its roof, this is
 * the line to change, not the palette below.
 */
const UP_AXIS = 2

// A overcast-bright day over asphalt, which is what a NYC train is actually under and
// also the classic chrome-shot setup: a big bright dome, a dark floor, and a hard
// bright line where they meet. Chrome reads as chrome because of that horizon line —
// it is the only feature a mirror on a smooth gradient has to catch.
const SKY_ZENITH: Rgb = [104, 141, 194]
const SKY_HIGH: Rgb = [158, 188, 226]
const HORIZON: Rgb = [238, 243, 248]
const GROUND_NEAR: Rgb = [92, 96, 104]
const GROUND_DEEP: Rgb = [34, 36, 41]

/** Half-width of the horizon flare, in units of the up-component. ~3.5° of arc. */
const HORIZON_FALLOFF = 0.06

const mixRgb = (a: Rgb, b: Rgb, t: number): Rgb => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
]

/**
 * What the specular cube holds: the environment as a mirror would see it, sharp.
 * `up` is the reflection's up-component in [-1, 1].
 *
 * The sky is raised to a fractional power so most of the dome sits near the bright
 * horizon end rather than spending half its range on zenith blue — a car body's
 * reflection is nearly all grazing angles, so a linear ramp puts the interesting part
 * of the gradient where nothing on the train can see it.
 */
function radiance(up: number): Rgb {
  const base =
    up >= 0
      ? mixRgb(HORIZON, SKY_ZENITH, Math.pow(up, 0.45))
      : mixRgb(GROUND_NEAR, GROUND_DEEP, Math.pow(-up, 0.5))
  const flare = Math.exp(-((up / HORIZON_FALLOFF) ** 2))
  return mixRgb(base, HORIZON, flare * 0.85)
}

/**
 * What the diffuse cube holds: the same environment convolved to irradiance, i.e. a
 * soft hemisphere blend with the horizon detail gone.
 *
 * Deliberately dimmer than `radiance`. This term multiplies base colour, so it is the
 * one that can wash out the gloss-black front cap — and it does nothing for the shell,
 * which is metal and therefore has no diffuse to light. Sky is pulled toward
 * `SKY_HIGH` rather than the horizon for the same reason.
 */
function irradiance(up: number): Rgb {
  return mixRgb(
    mixRgb(GROUND_DEEP, GROUND_NEAR, 0.5),
    mixRgb(SKY_HIGH, SKY_ZENITH, 0.35),
    0.5 + 0.5 * up,
  )
}

/**
 * Direction sampled by face `dir` at image coordinate (u, v), both in [-1, 1] with v
 * running DOWN the canvas. `dir` indexes luma's face order: +X −X +Y −Y +Z −Z.
 *
 * This is the OpenGL cubemap table written out rather than derived. A sign error here
 * mirrors the world in the shell and produces nothing more diagnostic than "the sky is
 * on the wrong side", so it is worth being able to read it straight off.
 */
function faceDirection(dir: number, u: number, v: number): [number, number, number] {
  switch (dir) {
    case 0:
      return [1, -v, -u]
    case 1:
      return [-1, -v, u]
    case 2:
      return [u, 1, v]
    case 3:
      return [u, -1, -v]
    case 4:
      return [u, -v, 1]
    default:
      return [-u, -v, -1]
  }
}

function cubeFaceCanvas(dir: number, size: number, shade: (up: number) => Rgb): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const image = ctx.createImageData(size, size)
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const d = faceDirection(dir, (2 * (x + 0.5)) / size - 1, (2 * (y + 0.5)) / size - 1)
      const [r, g, b] = shade(d[UP_AXIS] / Math.hypot(d[0], d[1], d[2]))
      const i = (y * size + x) * 4
      image.data[i] = r
      image.data[i + 1] = g
      image.data[i + 2] = b
      image.data[i + 3] = 255
    }
  }
  ctx.putImageData(image, 0, 0)
  return canvas
}

// The specular cube is sampled with an explicit LOD of `perceptualRoughness * 9`, but
// `loadPBREnvironment` builds it with a plain LINEAR min filter, so a texture without a
// mip chain resolves every LOD to level 0 — a perfect mirror at any roughness. That is
// the right answer for a train we WANT mirror-finish, and it is why only one level is
// generated. If a rougher body ever needs a blurred reflection, that is a mip chain
// plus a mipmap min filter, not a bigger level 0.
const SPECULAR_SIZE = 128
// Irradiance is a hemisphere-wide average with no feature narrower than the whole face.
const DIFFUSE_SIZE = 16

const srgbEncode = (v: number) =>
  v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055

/** Van der Corput radical inverse — the second Hammersley coordinate. */
function radicalInverse(bits: number): number {
  let b = bits
  b = ((b << 16) | (b >>> 16)) >>> 0
  b = (((b & 0x55555555) << 1) | ((b & 0xaaaaaaaa) >>> 1)) >>> 0
  b = (((b & 0x33333333) << 2) | ((b & 0xcccccccc) >>> 2)) >>> 0
  b = (((b & 0x0f0f0f0f) << 4) | ((b & 0xf0f0f0f0) >>> 4)) >>> 0
  b = (((b & 0x00ff00ff) << 8) | ((b & 0xff00ff00) >>> 8)) >>> 0
  return b * 2.3283064365386963e-10
}

const LUT_SIZE = 64
const LUT_SAMPLES = 128

/**
 * The split-sum BRDF lookup table: for each (NdotV, roughness) the scale and bias to
 * apply to F0, per Karis. Standard GGX importance sampling with the Smith IBL
 * geometry term — this is the same integral the reference `brdfLUT.png` shipped with
 * glTF-WebGL-PBR holds, computed rather than downloaded.
 *
 * Indexed by the shader as (NdotV, 1 - perceptualRoughness), so row 0 of the image is
 * roughness 1. 64² at 128 samples is ~0.5 M iterations, a few ms once per process, and
 * the surface is smooth enough that bilinear filtering hides the sample noise.
 */
function brdfLutCanvas(): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = LUT_SIZE
  canvas.height = LUT_SIZE
  const ctx = canvas.getContext('2d')!
  const image = ctx.createImageData(LUT_SIZE, LUT_SIZE)
  for (let y = 0; y < LUT_SIZE; y++) {
    // The shader looks this up at `1 - roughness`, so the top row is the rough end.
    const roughness = 1 - (y + 0.5) / LUT_SIZE
    const a = roughness * roughness
    for (let x = 0; x < LUT_SIZE; x++) {
      const nDotV = (x + 0.5) / LUT_SIZE
      const vx = Math.sqrt(1 - nDotV * nDotV)
      const vz = nDotV
      let scale = 0
      let bias = 0
      for (let i = 0; i < LUT_SAMPLES; i++) {
        // Importance-sample the GGX half-vector, tangent space with n = +z.
        const u1 = (i + 0.5) / LUT_SAMPLES
        const u2 = radicalInverse(i)
        const cosTheta = Math.sqrt((1 - u2) / (1 + (a * a - 1) * u2))
        const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta))
        const phi = 2 * Math.PI * u1
        const hx = sinTheta * Math.cos(phi)
        const hy = sinTheta * Math.sin(phi)
        const hz = cosTheta
        const vDotH = vx * hx + vz * hz
        const lz = 2 * vDotH * hz - vz
        if (lz <= 0) continue
        // Smith's separable geometry term with the IBL k = a/2.
        const k = a / 2
        const gv = vz / (vz * (1 - k) + k)
        const gl = lz / (lz * (1 - k) + k)
        const gVis = (gv * gl * Math.max(0, vDotH)) / (hz * nDotV)
        const fc = Math.pow(1 - Math.max(0, vDotH), 5)
        scale += (1 - fc) * gVis
        bias += fc * gVis
      }
      const i = (y * LUT_SIZE + x) * 4
      image.data[i] = Math.round(255 * srgbEncode(Math.min(1, scale / LUT_SAMPLES)))
      image.data[i + 1] = Math.round(255 * srgbEncode(Math.min(1, bias / LUT_SAMPLES)))
      image.data[i + 2] = 0
      image.data[i + 3] = 255
    }
  }
  ctx.putImageData(image, 0, 0)
  return canvas
}

const SAMPLER = {
  addressModeU: 'clamp-to-edge',
  addressModeV: 'clamp-to-edge',
  minFilter: 'linear',
  magFilter: 'linear',
} as const

function texture2d(device: Device, image: HTMLCanvasElement): Texture {
  const tex = device.createTexture({
    dimension: '2d',
    format: 'rgba8unorm',
    width: image.width,
    height: image.height,
    sampler: SAMPLER,
  })
  // No flipY: the LUT is written in the same top-down order the shader indexes it in.
  tex.copyExternalImage({ image, width: image.width, height: image.height })
  return tex
}

function textureCube(
  device: Device,
  size: number,
  shade: (up: number) => Rgb,
): Texture {
  const tex = device.createTexture({
    dimension: 'cube',
    format: 'rgba8unorm',
    width: size,
    height: size,
    sampler: SAMPLER,
  })
  for (let dir = 0; dir < 6; dir++) {
    // `z` selects the cube face and follows the GL face order (+X −X +Y −Y +Z −Z),
    // which is the order `faceDirection` is written in. No flipY, because a cube face
    // has its own texel orientation and flipping it mirrors the world.
    tex.copyExternalImage({
      image: cubeFaceCanvas(dir, size, shade),
      width: size,
      height: size,
      z: dir,
    })
  }
  return tex
}

// One environment per device. The layer prop is a callback because the device only
// exists once the map has a GL context, and buildVehicleLayers reconstructs its layers
// every frame — so this must be idempotent and must NOT re-upload three textures.
let cached: { device: Device; env: PBREnvironment } | null = null

/**
 * ⚠️ Built with plain `Texture`s rather than luma's `loadPBREnvironment`, which is the
 * documented path. That helper takes URLs and uploads through `DynamicTexture`, whose
 * init is async no matter how fast the source resolves — and the one moment this
 * environment is read is `parsePBRMaterial`, synchronously, the instant the glTF
 * finishes loading. Losing that race throws "Texture not initialized yet" and the layer
 * comes up with no IBL at all, permanently: `_getModelOptions` is only consulted when
 * `props.scenegraph` changes, so the model is parsed once and never reconsidered.
 * Uploading from canvases here is synchronous and cannot race. `Texture` carries
 * `ready`/`isReady` explicitly "for type compatibility with DynamicTexture", and the
 * consumer only ever reads `.texture` off these wrappers.
 */
export function r160PbrEnvironment(device: Device): PBREnvironment {
  if (cached?.device === device) return cached.env
  const env = {
    brdfLutTexture: { texture: texture2d(device, brdfLutCanvas()) },
    diffuseEnvSampler: { texture: textureCube(device, DIFFUSE_SIZE, irradiance) },
    specularEnvSampler: { texture: textureCube(device, SPECULAR_SIZE, radiance) },
  } as unknown as PBREnvironment
  cached = { device, env }
  return env
}
